import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { NEED_SENTENCE, type NeedKind } from "../home";
import { queueWord, useScoreRun } from "./ScoreRunContext";

// Inside a collection, the list is the CLASS.
//
// The middle column used to be the queue filtered down to this set: papers, sorted by state,
// under a row of count chips. Two things were wrong with it. A queue is made of papers, so the
// students who have handed in nothing could not appear in it — and they are exactly who "22 of 26"
// is a sentence about. And sorting by state meant a teacher looking for one student had to know
// what state that student's paper was in before they could find them.
//
// So: everyone, by name, once. What is waiting for a person is lifted into its own band above,
// because it is a different question ("what must I do") from the one the list answers ("how is
// this class doing") — and that band is absent, not empty, when there is nothing in it.

export interface Person {
  student_id: string | null;
  name: string;
  artifact_id: string | null;
  /** Null when nothing was handed in. Not a state meaning nothing — no paper to be in one. */
  state: string | null;
  state_reason_code?: string | null;
  needs: NeedKind | null;
  traits?: number;
  holds?: number;
  left_the_class?: boolean;
}

export interface Collection {
  activity_id: string;
  section_id: string;
  section_label: string;
  task_id: string | null;
  iteration: string | null;
  lesson_title: string | null;
  folder_url: string | null;
  folder_name: string | null;
  enrolled: number;
  handed_in: number;
  /** Papers in this folder that nobody is named on. Counted apart from `handed_in` rather than
   *  folded into it: one of them is probably a student the list shows as having handed in
   *  nothing, and saying "7 of 7" over that list is false about the cohort. */
  unnamed: number;
}

interface View {
  available: boolean;
  collection: Collection | null;
  people: Person[];
  unnamed: Person[];
}

/** What a paper's state means to the person reading it. `null` is not a state — it is the absence
 *  of a paper, and saying so is the point of listing the student at all. */
const STATE_WORD: Record<string, string> = {
  unbound: "nobody named",
  // HANDED IN comes first in each of these, because that is the fact a teacher checks the list
  // for. "Scoring" said what the machine was doing and left the student's side of it unsaid —
  // and beside a panel with no paper on it, read as though nothing had arrived.
  bound: "handed in — not scored yet",
  scored: "handed in — being scored",
  composed: "handed in — being scored",
  in_review: "scored — ready for your review",
  // NOT "scoring stopped". `composed -> blocked` is the DRAFTED MESSAGE failing a check; the scores
  // came back and are complete. The old word blamed the stage that worked and left the one that
  // needs a person unnamed.
  blocked: "scored — the draft was held",
  not_scorable: "cannot be scored",
  released: "handed back",
  withheld: "held back",
};

function stateWord(p: Person): string {
  if (!p.artifact_id) return "nothing handed in";
  return STATE_WORD[p.state ?? ""] ?? p.state ?? "";
}

export function CollectionPeople({ activityId, selected, selectedStudent, reload, onSelect,
                                   onLoaded }: {
  activityId: string;
  /** The artifact on screen, so the row it belongs to is marked. */
  selected: string | null;
  /** The student on screen who has no paper. Marked by STUDENT, because `artifact_id === selected`
   *  is `null === null` for every one of them the moment nothing is selected — which lit up every
   *  student who had handed in nothing as though they were all open at once. */
  selectedStudent: string | null;
  /** Bumped by the console whenever it changes a paper, so this list says what is now true. */
  reload: number;
  onSelect: (p: Person) => void;
  /** The collection itself, for whatever else on the screen names it. */
  onLoaded?: (c: Collection | null) => void;
}) {
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.get<View>(`/review/people?activity_id=${encodeURIComponent(activityId)}`)
      .then((v) => { if (live) { setView(v); setError(null); onLoaded?.(v.collection); } })
      .catch((e: unknown) => {
        if (live) { setView(null); setError(e instanceof ApiError ? e.message : "could not be read"); }
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId, reload]);

  const { state: run } = useScoreRun();

  const isOpen = (p: Person) => (p.artifact_id
    ? p.artifact_id === selected
    : Boolean(selectedStudent) && p.student_id === selectedStudent);

  if (error) return <p className="rv-mut">{error}</p>;
  if (!view) return <p className="rv-mut">Reading the class…</p>;

  // ONCE EACH, AND ABOVE THE LINE. An unnamed paper was in both lists — in `waiting` because it
  // needs a person, and in `everyone` because `everyone` concatenated it. Twenty papers rendered
  // as forty rows, the same rows twice, under two headings.
  //
  // Below the line is student information. A paper nobody is named on has no student, so it is not
  // a row in the class; it is a thing waiting on somebody, which is what the band above is for.
  // Naming it moves it down, which is the whole point of naming it.
  const everyone = view.people;
  // Papers nobody is named on come first: until someone says whose a paper is, nothing about it
  // can be scored or handed back, so it blocks more than a held criterion does.
  const waiting = [...view.unnamed, ...view.people].filter((p) => p.needs);

  return (
    <>
      {waiting.length > 0 && (
        <section className="cp-band">
          {/* Counted, because twenty is a different morning from two and the band used to say
              neither. */}
          <h3>Needs you <span className="cp-of">{waiting.length}</span></h3>
          <ul className="cp-needs">
            {waiting.map((p) => (
              <li key={p.artifact_id ?? p.student_id}>
                <button className={isOpen(p) ? "cp-sel" : ""} onClick={() => onSelect(p)}>
                  <b>{p.name}</b>
                  {/* What happened, in a sentence. The same words the landing screen uses for
                      the same evidence — one vocabulary, because a teacher reading "Two raters
                      disagreed" in one place and "mismatch" in another has to learn twice. */}
                  <span className="cp-why">{NEED_SENTENCE[p.needs as NeedKind]}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* BELOW THE LINE: the cohort, one row per student. Above it is the folder — which lesson,
          which section, what is waiting on you. They were stacked with nothing between them, so
          the folder's own title read as the first name in the list. */}
      <section className="cp-band rv-students">
        {/* Counted against enrolment, which is what the number means. Not a percentage: four
            people have not handed in, and 85% is not something anyone can act on. */}
        <h3>
          Students
          {/* "0 of 0 in" — a literal `in` with nothing after it, which read as a sentence cut
              off mid-word, and read wrong with data too ("3 of 12 in · 2 not named"). Hidden
              entirely for a cohort with nobody in it: a count of nothing is not a count. */}
          {view.collection && view.collection.enrolled > 0 && (
            <span className="cp-of">
              {view.collection.handed_in} of {view.collection.enrolled} handed in
              {view.collection.unnamed > 0
                && ` · ${view.collection.unnamed} not named`}
            </span>
          )}
        </h3>
        <ul className="cp-people">
          {everyone.map((p) => (
            <li key={p.artifact_id ?? p.student_id}>
              <button className={isOpen(p) ? "cp-sel" : ""} onClick={() => onSelect(p)}>
                <b className={p.artifact_id ? "" : "cp-absent"}>{p.name}</b>
                {/* QUEUED SAYS MORE THAN UNSCORED, and only when it is true. `bound` means the
                    paper is ready to be scored, not that anybody has pressed Score — so the word
                    changes only while a run is actually in flight, and the title says where in
                    the line this paper is. */}
                {(() => {
                  const q = queueWord(run, p.artifact_id, p.state ?? null);
                  return q
                    ? <span className="cp-state cp-queued" title={q.detail}>{q.word}</span>
                    : <span className="cp-state">{stateWord(p)}</span>;
                })()}
                {/* Somebody who has left the cohort still has their work here, and the row says so
                    rather than leaving a name nobody recognises on the register. */}
                {p.left_the_class && <span className="cp-left">left the cohort</span>}
              </button>
            </li>
          ))}
        </ul>
        {everyone.length === 0 && (
          <p className="rv-mut">Nobody is enrolled in this cohort yet.</p>
        )}
      </section>
    </>
  );
}
